import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Albums, Folder } from "../../../../Data/album.dto";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Hooks";
import {
  lockAlbum as lockAlbumAPI,
} from "../../../../Redux/ApiCalls/Dashboard/AlbumAPI";
import ReasonModal from "./ReasonModal";
import {
  getFoldersForAlbum,
  lockMultipleFoldersAPI,
  unlockFolderAPI,
} from "../../../../Redux/ApiCalls/Dashboard/FolderApi";
import { clearError, clearFlagAlbums } from "../../../../Redux/Slice/Dashboard/AlbumSlice";
import {
  X,
  Sparkles,
  Lock,
  Unlock,
  FolderLock,
  Loader2,
  CheckCircle2,
} from "lucide-react";

interface LockAlbumModalProps {
  album: Albums;
  setShowModal: (flag: boolean) => void;
}

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const LockAlbumModal: React.FC<LockAlbumModalProps> = ({ album, setShowModal }) => {
  const [selectedFolders, setSelectedFolders] = useState<number[]>([]);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reason, setReason] = useState("");
  const [selectedReason, setSelectedReason] = useState(0);
  const [lockAlbum, setLockAlbum] = useState(false);
  const [activeButton, setActiveButton] = useState(true);
  const [folders, setFolders] = useState<Folder[] | []>([]);
  const { folderLoading, albums, isFolderChange } = useAppSelector(
    (state) => state.album
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (album) {
      dispatch(getFoldersForAlbum(album.id));
    }
  }, []);

  useEffect(() => {
    handleSubmitButtonStatus();
  }, [lockAlbum, selectedFolders]);

  useEffect(() => {
    if (isFolderChange && album) {
      dispatch(getFoldersForAlbum(album.id));
    }
    return () => {
      dispatch(clearError());
      dispatch(clearFlagAlbums());
    };
  }, [isFolderChange]);

  useEffect(() => {
    albums.forEach((loopAlbum: Albums) => {
      if (loopAlbum.id === album.id) {
        if (loopAlbum.folders) {
          setFolders(loopAlbum.folders);
        }
      }
    });
  }, [albums]);

  const handleFolderChange = (folder: number) => {
    if (!lockAlbum)
      setSelectedFolders((prev) =>
        prev.includes(folder) ? prev.filter((f) => f !== folder) : [...prev, folder]
      );
  };

  const handleSubmitButtonStatus = () => {
    if (lockAlbum) {
      setActiveButton(false);
    } else {
      if (selectedFolders.length > 0) {
        setActiveButton(false);
      } else {
        setActiveButton(true);
      }
    }
  };

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedReason(parseInt(event.target.value));
  };

  const handleReasonSubmit = () => {
    if (lockAlbum) {
      if (selectedReason === 4) {
        dispatch(
          lockAlbumAPI({
            project_id: album.id,
            custom_reason: reason,
            lock_type: "lock",
          })
        );
      } else {
        dispatch(
          lockAlbumAPI({
            project_id: album.id,
            reason: selectedReason,
            lock_type: "lock",
          })
        );
      }
    } else {
      if (selectedReason === 4) {
        dispatch(
          lockMultipleFoldersAPI({
            folders: selectedFolders,
            project_id: album.id,
            custom_reason: reason,
          })
        );
      } else {
        dispatch(
          lockMultipleFoldersAPI({
            folders: selectedFolders,
            project_id: album.id,
            reason: selectedReason,
          })
        );
      }
    }

    setShowReasonModal(false);
    setShowModal(false);
  };

  const unlockAlbumFunc = (albumId: number) => {
    dispatch(lockAlbumAPI({ project_id: albumId, lock_type: "unlock" }));
  };

  const unlockFolder = (folderId: number) => {
    dispatch(
      unlockFolderAPI({
        folder_id: folderId,
        project_id: album.id,
        lock_type: "unlock",
      })
    );
  };

  const canLock = !activeButton;

  return (
    <>
      <Overlay onClick={() => setShowModal(false)}>
        <ModalBox onClick={(e) => e.stopPropagation()}>
          <ModalHero>
            <HeroGlow />
            <HeroTop>
              <HeroIcon>
                <FolderLock size={22} />
              </HeroIcon>
              <HeroText>
                <HeroBadge>
                  <Sparkles size={11} />
                  Album security
                </HeroBadge>
                <ModalTitle>Lock album</ModalTitle>
                <ModalSubtitle>
                  Restrict access to <strong>{album.name}</strong> or selected folders.
                </ModalSubtitle>
              </HeroText>
              <CloseBtn
                type="button"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              >
                <X size={18} />
              </CloseBtn>
            </HeroTop>
          </ModalHero>

          <ModalBody>
            {album.is_locked ? (
              <LockedBanner>
                <LockedBannerLeft>
                  <Lock size={16} />
                  <div>
                    <LockedTitle>Album already locked</LockedTitle>
                    <LockedHint>Unlock to restore access for clients.</LockedHint>
                  </div>
                </LockedBannerLeft>
                <UnlockBtn type="button" onClick={() => unlockAlbumFunc(album.id)}>
                  <Unlock size={14} />
                  Unlock
                </UnlockBtn>
              </LockedBanner>
            ) : (
              <EntireAlbumToggle
                type="button"
                $active={lockAlbum}
                onClick={() => setLockAlbum((value) => !value)}
              >
                <ToggleLeft>
                  <ToggleIcon $active={lockAlbum}>
                    <Lock size={16} />
                  </ToggleIcon>
                  <div>
                    <ToggleTitle>Lock entire album</ToggleTitle>
                    <ToggleHint>Locks all folders in one step</ToggleHint>
                  </div>
                </ToggleLeft>
                <ToggleSwitch $active={lockAlbum}>
                  <ToggleKnob $active={lockAlbum} />
                </ToggleSwitch>
              </EntireAlbumToggle>
            )}

            <SectionLabel>
              <FolderLock size={12} />
              Folders
              {selectedFolders.length > 0 && !lockAlbum && (
                <CountBadge>{selectedFolders.length} selected</CountBadge>
              )}
            </SectionLabel>

            {folderLoading ? (
              <LoadingWrap>
                <Spinner size={28} color="#c94daa" />
                Loading folders…
              </LoadingWrap>
            ) : folders.length === 0 ? (
              <EmptyState>No folders found in this album.</EmptyState>
            ) : (
              <FolderList>
                {folders.map((folder) => (
                  <FolderItem
                    key={folder.id}
                    $locked={folder.is_locked}
                    $selected={!folder.is_locked && (lockAlbum || selectedFolders.includes(folder.id))}
                    onClick={() => {
                      if (!folder.is_locked && !lockAlbum) handleFolderChange(folder.id);
                    }}
                  >
                    <FolderLeft>
                      {folder.is_locked ? (
                        <LockedIconWrap>
                          <Lock size={14} />
                        </LockedIconWrap>
                      ) : (
                        <CheckBox
                          type="checkbox"
                          id={`folderCheckbox${folder.id}`}
                          checked={lockAlbum || selectedFolders.includes(folder.id)}
                          disabled={lockAlbum}
                          onChange={() => handleFolderChange(folder.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <FolderName htmlFor={`folderCheckbox${folder.id}`}>
                        {folder.name}
                      </FolderName>
                    </FolderLeft>
                    {folder.is_locked && (
                      <UnlockBtn
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          unlockFolder(folder.id);
                        }}
                      >
                        <Unlock size={14} />
                        Unlock
                      </UnlockBtn>
                    )}
                  </FolderItem>
                ))}
              </FolderList>
            )}
          </ModalBody>

          <ActionRow>
            <CancelBtn type="button" onClick={() => setShowModal(false)}>
              Cancel
            </CancelBtn>
            <SubmitBtn
              type="button"
              disabled={!canLock}
              onClick={() => setShowReasonModal(true)}
            >
              <CheckCircle2 size={16} />
              Lock
            </SubmitBtn>
          </ActionRow>
        </ModalBox>
      </Overlay>

      {showReasonModal && (
        <ReasonModal
          selectedReason={selectedReason}
          handleSubmit={handleReasonSubmit}
          setShowReasonModal={setShowReasonModal}
          setReason={setReason}
          handleReasonChange={handleReasonChange}
          reason={reason}
        />
      )}
    </>
  );
};

export default LockAlbumModal;

const gradient =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  z-index: 10001;
  padding: 16px;
`;

const ModalBox = styled.div`
  width: 100%;
  max-width: 560px;
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
`;

const ModalHero = styled.div`
  position: relative;
  overflow: hidden;
  padding: 22px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.14) 0%,
    rgba(206, 86, 176, 0.1) 55%,
    rgba(53, 183, 200, 0.06) 100%
  );
  border-bottom: 1px solid rgba(201, 77, 170, 0.1);
  flex-shrink: 0;
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -50px;
  right: -20px;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.25) 0%, transparent 70%);
`;

const HeroTop = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const HeroIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${gradient};
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.25);
`;

const HeroText = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #c94daa;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #171717;
  line-height: 1.2;
`;

const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.58);
  strong {
    font-weight: 700;
    color: #171717;
  }
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.6);
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover {
    color: #c94daa;
    border-color: rgba(201, 77, 170, 0.25);
  }
`;

const ModalBody = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LockedBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(240, 154, 62, 0.1);
  border: 1px solid rgba(240, 154, 62, 0.28);
`;

const LockedBannerLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #f09a3e;
  min-width: 0;
`;

const LockedTitle = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: #171717;
`;

const LockedHint = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
  margin-top: 2px;
`;

const EntireAlbumToggle = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid
    ${(p) => (p.$active ? "rgba(201, 77, 170, 0.3)" : "rgba(23, 23, 23, 0.08)")};
  background: ${(p) => (p.$active ? "rgba(201, 77, 170, 0.08)" : "#fafafa")};
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
  }
`;

const ToggleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const ToggleIcon = styled.div<{ $active: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => (p.$active ? "#fff" : "#c94daa")};
  background: ${(p) => (p.$active ? gradient : "rgba(201, 77, 170, 0.1)")};
`;

const ToggleTitle = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: #171717;
`;

const ToggleHint = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.5);
  margin-top: 2px;
`;

const ToggleSwitch = styled.div<{ $active: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 999px;
  background: ${(p) => (p.$active ? gradient : "rgba(23, 23, 23, 0.15)")};
  position: relative;
  flex-shrink: 0;
  transition: background 0.2s ease;
`;

const ToggleKnob = styled.div<{ $active: boolean }>`
  position: absolute;
  top: 3px;
  left: ${(p) => (p.$active ? "23px" : "3px")};
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: left 0.2s ease;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const CountBadge = styled.span`
  margin-left: auto;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(201, 77, 170, 0.1);
  color: #c94daa;
  font-size: 11px;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;
`;

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 160px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: 24px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px dashed rgba(23, 23, 23, 0.1);
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.45);
`;

const FolderList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 280px;
  overflow-y: auto;
`;

const FolderItem = styled.li<{ $locked?: boolean; $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid
    ${(p) =>
      p.$selected
        ? "rgba(201, 77, 170, 0.28)"
        : p.$locked
          ? "rgba(240, 154, 62, 0.22)"
          : "rgba(23, 23, 23, 0.08)"};
  background: ${(p) =>
    p.$selected
      ? "rgba(201, 77, 170, 0.06)"
      : p.$locked
        ? "rgba(240, 154, 62, 0.05)"
        : "#fafafa"};
  cursor: ${(p) => (p.$locked ? "default" : "pointer")};
  transition: all 0.15s ease;
`;

const FolderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

const CheckBox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #c94daa;
  cursor: pointer;
  flex-shrink: 0;
`;

const LockedIconWrap = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(240, 154, 62, 0.14);
  color: #f09a3e;
  flex-shrink: 0;
`;

const FolderName = styled.label`
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #171717;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UnlockBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.7);
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 16px 24px 22px;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
  flex-shrink: 0;
`;

const CancelBtn = styled.button`
  flex: 0 0 auto;
  padding: 0 20px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.65);
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
`;

const SubmitBtn = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.25);
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
`;
